import React from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import { Settings } from '@mui/icons-material';
import SearchBox from './SearchBox';
import ViewToggle from './ViewToggle';
import PaginationControls from './PaginationControls';

/**
 * Reusable Page Header Component - Odoo style
 * Used across all list pages (Customers, Products, Contacts, Quotations, etc.)
 * 
 * @param {string} title - Page title (e.g., "Customers", "Products")
 * @param {string} newButtonText - Text for new button (default: "New")
 * @param {function} onNew - Callback when new button is clicked
 * @param {boolean} showNewButton - Whether to show new button (default: true)
 * @param {string} search - Current search value
 * @param {function} onSearchChange - Callback when search changes
 * @param {string} searchPlaceholder - Search placeholder text
 * @param {boolean} showSearch - Whether to show search box (default: true)
 * @param {string} viewMode - Current view mode ('list', 'kanban', 'calendar', etc.)
 * @param {function} onViewModeChange - Callback when view mode changes
 * @param {array} viewModes - Available view modes
 * @param {boolean} showViewToggle - Whether to show view toggle (default: true)
 * @param {number} page - Current page number (0-indexed)
 * @param {number} rowsPerPage - Rows per page
 * @param {number} total - Total number of items
 * @param {function} onPageChange - Callback when page changes
 * @param {boolean} showPagination - Whether to show pagination (default: true)
 * @param {function} onSettingsClick - Callback when settings icon is clicked
 * @param {boolean} showSettings - Whether to show settings icon (default: true)
 * @param {React.ReactNode} additionalActions - Additional actions to render
 */
const PageHeader = ({
  title,
  newButtonText = 'New',
  onNew,
  showNewButton = true,
  search = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  showSearch = true,
  viewMode = 'list',
  onViewModeChange,
  viewModes = ['list', 'kanban'],
  showViewToggle = true,
  page = 0,
  rowsPerPage = 10,
  total = 0,
  onPageChange,
  showPagination = true,
  onSettingsClick,
  showSettings = true,
  additionalActions,
  primaryColor = '#875A7B',
}) => {
  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid #dee2e6',
        px: { xs: 1, sm: 2 },
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        gap: 1,
        minHeight: 48,
      }}
    >
      {/* Left side - New button and Title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, order: { xs: 1, md: 1 } }}>
        {showNewButton && onNew && (
          <Button
            variant="contained"
            onClick={onNew}
            sx={{
              backgroundColor: primaryColor,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              px: 2.5,
              py: 0.75,
              minHeight: 34,
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              '&:hover': { 
                backgroundColor: adjustColor(primaryColor, -15), 
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)' 
              },
            }}
          >
            {newButtonText}
          </Button>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ 
              fontSize: '14px', 
              color: '#333', 
              fontWeight: 400, 
              display: { xs: 'none', sm: 'block' } 
            }}
          >
            {title}
          </Typography>
          {showSettings && (
            <IconButton 
              size="small" 
              sx={{ 
                p: 0.5,
                color: '#999',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(135, 90, 123, 0.1)',
                  color: '#875A7B',
                  transform: 'rotate(30deg)',
                },
              }}
              onClick={onSettingsClick}
            >
              <Settings sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Center - Search */}
      {showSearch && (
        <SearchBox
          value={search}
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
          sx={{
            order: { xs: 3, md: 2 },
            flexBasis: { xs: '100%', md: 'auto' },
          }}
        />
      )}

      {/* Right side - View toggles and Pagination */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, order: { xs: 2, md: 3 } }}>
        {additionalActions}
        
        {showViewToggle && onViewModeChange && (
          <ViewToggle
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
            viewModes={viewModes}
          />
        )}

        {showPagination && onPageChange && total > 0 && (
          <PaginationControls
            page={page}
            rowsPerPage={rowsPerPage}
            total={total}
            onPageChange={onPageChange}
          />
        )}
      </Box>
    </Box>
  );
};

// Helper function to adjust color brightness
const adjustColor = (color, percent) => {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
};

export default PageHeader;
